/**
 * Caster LOD from the view camera (scale build SC-6a1; `environment.json shadows.casterLodFromView`, per preset).
 *
 * three r186 calls `LOD.update(camera)` in every pass that projects the scene (`Renderer.js:3256-3258`): the AO
 * prepass, the scene pass and each shadow level, each with its own camera. A shadow level's camera sits
 * `half + casterMargin` sunward of the ship (`shadowFit.ts`), so it picks a coarser level for the ship and the
 * near rocks than the view does (the Kestrel's self-shadow caster drops to LOD2-LOD3 on every preset), and every
 * shadow pass overwrites the level visibility that three's hysteresis reads in the next view pass.
 *
 * `ViewLOD` is the model LOD every builder uses (`assets/loader.ts`, `assets/standIn.ts`, the derelict's merged
 * LOD). With the pin on and a frame begun (`beginViewLodFrame`, once per frame in `app/loop.ts` after the scene's
 * world-matrix walk and before `post.render()`), the first projection of the frame picks the level from the view
 * camera and every later pass of that frame (prepass, scene, shadow levels) reuses it: one pick per frame, from the
 * view, so shadow casters draw what the player sees and hysteresis is never reset by another camera. Nothing moves
 * a scene object or the camera between `beginViewLodFrame` and the passes (PERF L-M1), so the lazy pick equals a
 * pick at that point. With the pin off (or no frame begun: lookdev, tests) `update` is three's own, per pass.
 *
 * Per-frame code allocates nothing (D-014).
 */
import { LOD, type Camera } from 'three/webgpu'

interface ViewLodPin {
  /** The preset's `shadows.casterLodFromView` (set by the environment on every preset change). */
  enabled: boolean
  /** The view camera of the current frame; null until the game loop begins a frame. */
  camera: Camera | null
  /** Frame counter: a pinned LOD updates once per value. */
  frame: number
}

const pin: ViewLodPin = { enabled: false, camera: null, frame: 0 }

/** Turns the pin on or off (the preset's `shadows.casterLodFromView`). Off = three's per-pass picking. */
export function setCasterLodFromView(on: boolean): void {
  pin.enabled = on
}

/** True while model LODs pick their level once per frame from the view camera. */
export function casterLodFromView(): boolean {
  return pin.enabled
}

/**
 * Begins a frame: pinned LODs pick their level once from `camera` at their first projection after this call.
 * Call once per frame after the last mover and the world-matrix walk, before any pass. Allocation-free.
 */
export function beginViewLodFrame(camera: Camera): void {
  // The renderer refreshes a parentless camera only when it renders with it; a shadow pass could come first.
  if (camera.parent === null && camera.matrixWorldAutoUpdate) camera.updateMatrixWorld()
  pin.camera = camera
  pin.frame++
}

/** Back to the boot state (tests). */
export function resetViewLodPin(): void {
  pin.enabled = false
  pin.camera = null
  pin.frame = 0
}

/** A three `LOD` whose level is picked once per frame from the view camera while the pin is on (module comment). */
export class ViewLOD extends LOD {
  private pinnedFrame = -1

  override update(camera: Camera): void {
    const view = pin.camera
    if (!pin.enabled || view === null) {
      super.update(camera)
      return
    }
    if (this.pinnedFrame === pin.frame) return
    this.pinnedFrame = pin.frame
    super.update(view)
  }
}
